import { IGif } from '@giphy/js-types'
import { VolumeOffIcon, VolumeOnIcon } from '@giphy/react-components'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useInView } from 'react-intersection-observer'
import { useWindowSize } from 'react-use'
import Video_ from 'shared/components/video-player/sdk-video-with-ga'
import styled from 'styled-components'
import Menu from './menu'

const Video = styled(Video_)`
    overflow: hidden;
`

type Props = {
    gif: IGif
    showAttribution?: boolean
}

const Container = styled.div`
    position: relative;
`

const Mute = styled.div`
    position: absolute;
    top: 10px;
    right: 10px;
    cursor: pointer;
`

const MUTE_AFTER_LOOPS = 3
export const GACategory = 'Video Embed'
const VideoEmbed = ({ gif }: Props) => {
    // remove the ARP as requested to stop pingbacks
    // https://giphypedia.atlassian.net/browse/SDK-909
    gif.analytics_response_payload = ''

    const videoRef = useRef<HTMLVideoElement>()
    const { width, height } = useWindowSize()
    const [userMuted, setUserMuted] = useState<boolean>()
    const [showCaptions, setShowCaptions] = useState(false)
    const [ref, inView] = useInView()
    const [muteLoopCount, setMuteLoopCount] = useState(0)

    const postMessagingReceiver = useCallback(
        ({ data }) => {
            if (data?.action === 'mute') {
                setUserMuted(true)
            }

            if (data?.action === 'unmute') {
                setUserMuted(false)
            }

            if (data?.action === 'play') {
                videoRef.current?.play()
            }

            if (data?.action === 'pause') {
                videoRef.current?.pause()
            }

            if (data?.action === 'stop') {
                videoRef.current?.pause()
                if (videoRef.current) {
                    videoRef.current.currentTime = 0
                }
            }

            if (data?.action === 'seekTo') {
                if (videoRef.current && data.value) {
                    videoRef.current.currentTime = data.value
                }
            }
        },
        [setUserMuted]
    )

    useEffect(() => {
        window.addEventListener('message', postMessagingReceiver)

        return () => {
            window.removeEventListener('message', postMessagingReceiver)
        }
    }, [])

    // the overlay menu
    const Overlay = useCallback(
        ({ isHovered }) => (
            <>
                <Menu
                    isHovered={isHovered}
                    gif={gif}
                    toggleCaptions={() => {
                        setShowCaptions(!showCaptions)
                    }}
                />
                <Mute onClick={() => setUserMuted(!userMuted)}>
                    {userMuted ? <VolumeOffIcon /> : isHovered ? <VolumeOnIcon /> : null}
                </Mute>
            </>
        ),
        [showCaptions, gif, userMuted]
    )

    return (
        <Container ref={ref}>
            <Video
                gif={gif}
                setVideoEl={(video) => {
                    videoRef.current = video
                }}
                ccEnabled={showCaptions}
                muted={inView ? userMuted : true}
                width={width}
                height={height}
                hideMute
                onMuted={(browserMuted) => {
                    setUserMuted(browserMuted)
                }}
                onUserMuted={(muted) => {
                    setUserMuted(muted)
                    if (!muted) {
                        setMuteLoopCount(0)
                    }
                }}
                controls
                onLoop={() => {
                    if (muteLoopCount === MUTE_AFTER_LOOPS) {
                        setUserMuted(true)
                    }
                    setMuteLoopCount(muteLoopCount + 1)
                }}
                category={GACategory}
                overlay={Overlay}
            />
        </Container>
    )
}

export default VideoEmbed
